import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { deselectCompareProduct } from '@/utils/Common/ListingUtils';
import { hasImageSizes } from '@/utils/Common/Utils';

// Context
import { useCompareContext } from '@/context/compare';

// Components
import CompareProductReviews from '@/braun/components/molecules/CompareProductReviews';
import EVENTS from '@/utils/Constants/Events';

const CompareProduct = ({ productData }) => {
  const [{ compareOpen }] = useCompareContext();
  const wrapperRef = useRef();
  const [images, setImages] = useState(null);
  const { t } = useTranslation();
  const {
    name,
    code,
    baseOptions,
    url,
    dlgImages,
    materialGroup,
    numberOfReviews,
    averageRating,
  } = productData;

  const price = baseOptions?.[0]?.selected?.priceData?.formattedValue;

  const productUrl = `${DLG.config.encodedContextPath}${url}`;
  const [, { pushProductCompare }] = useCompareContext();
  const removeProduct = (productCode, material) => {
    pushProductCompare({ productCode, materialGroup: material, push: false });
    deselectCompareProduct(productCode);
  };

  const toggleCompare = isExpanded => {
    setImages(dlgImages[isExpanded ? 0 : 1]);
  };

  useEffect(() => {
    if (dlgImages) {
      setImages(dlgImages[compareOpen ? 0 : 1]);
      DLG.EVE.on(EVENTS.LISTING.COMPARE.TOGGLE, toggleCompare);
    }
    return () => {
      DLG.EVE.off(EVENTS.LISTING.COMPARE.TOGGLE, toggleCompare);
    };
  }, [dlgImages]);

  return (
    <div
      ref={wrapperRef}
      className='bra-prodTile bra-prodTile--compare bra-prodTile--collapsed'
    >
      <div className='bra-prodTile__product'>
        <div className='bra-prodTile__head'>
          <button
            type='button'
            className='bra-btnIcon'
            onClick={() => removeProduct(code, materialGroup)}
          >
            <span className='bra-btnIcon__wrap'>
              <i className='bra-icn bra-icn__no' />
            </span>
          </button>
        </div>
        <a className='bra-prodTile__imgWrap' href={productUrl} rel='nofollow'>
          {hasImageSizes(images) && (
            <>
              <picture className='bra-prodTile__img'>
                <source srcSet={images.l.url} media='(min-width:1200px)' />
                <source srcSet={images.m.url} media='(min-width:1024px)' />
                <source srcSet={images.s.url} media='(min-width:768px)' />
                <img
                  className='bra-prodTile__imgFile'
                  src={images.xs.url}
                  alt={images.altText}
                />
              </picture>
              <span className='bra-prodTile__imgOverlay' />
            </>
          )}
        </a>
        <a className='bra-prodTile__productInfo' href={productUrl} rel='follow'>
          <h3 className='bra-prodTile__name'>{name}</h3>
          <p className='bra-prodTile__type'>{code}</p>
        </a>
        <p className='bra-prodTile__priceWrap'>
          <span className='bra-prodTile__price'>{price}</span>
        </p>
        <div className='bra-prodTile__foot'>
          {DLG.config.selectedReviewsSource === 'BAZAARVOICE' && (
            <div data-bv-show='inline_rating' data-bv-productId={code} />
          )}
          {DLG.config.selectedReviewsSource === 'OOTB' && (
            <CompareProductReviews
              numberOfReviews={numberOfReviews}
              averageRating={averageRating}
            />
          )}
          <div className='bra-prodTile__desktop'>
            <a
              href={productUrl}
              className='bra-btn bra-btn--rounded bra-btn--bordered js-gtm-comparison-tool-link-in-listing'
            >
              {t('listing:listing.compare.product.cta.label')}
            </a>
          </div>
          <div className='bra-prodTile__mobile'>
            <a
              href={productUrl}
              className='bra-btn bra-btn--rounded bra-btn--bordered js-gtm-comparison-tool-link-in-listing'
            >
              {t('listing:listing.compare.product.cta.label')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

CompareProduct.propTypes = {
  productData: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default CompareProduct;
